/**
 * The pre-yes catalog (R1a re-review 2, catalog audit): before a yes, a recipe presses only a
 * chord, walks only a menu path, closes only a switcher and opens only a link that this catalog
 * vets for its app. Everything else waits for the person's yes (os-recipe-lint.ts). It lives in
 * the runner's own bundle, never in a program, so a server cannot widen it; and it is read only
 * through the functions below (the data is module-private and frozen), so nothing adds to it at
 * run time. A recipe that needs a new pre-yes entry waits for a runner bundle (minutes).
 *
 * Harvested from the research's `steps` (research/{comms,web,apple,personal}/*.md) plus the
 * shipped table, then reviewed one by one: kept are navigation, new tab/window, find, go-to, view
 * toggles and zoom; left out is anything that calls, shares, records, unmutes, sends, reacts,
 * opens a conversation (a read receipt), publishes a status, deletes, cuts, prints, saves, exports
 * or edits a document, and any command palette. A browser chord lands in whatever web app is key,
 * so Chrome and Safari keep only chords harmless in Meet, Teams, Google Docs, Gmail and Slack web:
 * tabs, find, zoom, the address bar, copy and settings.
 *
 * Bare printable keys are listed too (a letter is an app's shortcut); each still needs the focus
 * guard first. Bare navigation keys (arrows, tab, escape, page and home/end) need no listing.
 * Chords are canonical: modifiers sorted (cmd, ctrl, opt, shift), then the key. Menu paths are the
 * app's English titles joined by " > ": v1 is English only, and a localized path waits for a yes.
 */

const freeze = <T>(v: T): Readonly<T> => Object.freeze(v);
const digits = (prefix: string): string[] => ['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => `${prefix}${d}`);

/** The chords each app may be sent before a yes, by bundle id. */
const CHORDS: Readonly<Record<string, readonly string[]>> = freeze({
  'com.tinyspeck.slackmacgap': freeze([
    'cmd+shift+a', 'cmd+shift+k', 'cmd+[', 'cmd+k', 'cmd+shift+t', 'cmd+shift+m', 'ctrl+1', 'cmd+shift+l', 'cmd+shift+e',
    'opt+shift+down', 'opt+shift+up', 'opt+down', 'opt+up', 'cmd+j', 'cmd+shift+i', 'cmd+.', 'ctrl+tab', 'ctrl+shift+tab',
    ...digits('cmd+'), 'cmd+g', 'cmd+f', 'cmd+n', 'cmd+,', 'cmd+shift+j', 'cmd+o',
  ]),
  'com.apple.mail': freeze([
    'cmd+n', 'cmd+r', 'cmd+shift+r', 'cmd+shift+f', 'cmd+shift+n', 'cmd+opt+f', 'cmd+shift+o', 'cmd+0', 'cmd+o', 'cmd+opt+1', 'cmd+opt+2', 'cmd+opt+3',
    'cmd+opt+4', 'cmd+opt+5',
  ]),
  'com.apple.MobileSMS': freeze(['cmd+n', 'cmd+f', 'cmd+ctrl+1', 'cmd+ctrl+2', 'cmd+ctrl+3', 'cmd+ctrl+5', 'cmd+opt+i', 'cmd+opt+b', 'cmd+0', 'cmd+r', 'cmd+,', 'cmd+opt+e']),
  'net.whatsapp.WhatsApp': freeze(['cmd+f', 'cmd+,']),
  'us.zoom.xos': freeze(['cmd+,', 'cmd+u']),
  'com.google.Chrome': freeze([
    'cmd+t', 'cmd+shift+t', 'cmd+opt+right', 'cmd+opt+left', ...digits('cmd+'), 'cmd+f', 'cmd+g', 'cmd+shift+g', 'cmd+=', 'cmd+-', 'cmd+0',
    'cmd+l', 'cmd+c', 'cmd+,',
  ]),
  'com.apple.Safari': freeze(['cmd+t', 'cmd+shift+t', 'ctrl+tab', 'ctrl+shift+tab', 'cmd+f', 'cmd+g', 'cmd+=', 'cmd+-', 'cmd+0', 'cmd+l', 'cmd+,']),
  'notion.id': freeze([
    'cmd+p', 'cmd+shift+u', 'cmd+n', 'cmd+t', 'cmd+shift+t', 'ctrl+tab', 'ctrl+shift+tab', ...digits('cmd+'), 'cmd+shift+n', 'cmd+\\',
    'cmd+shift+l', 'cmd+=', 'cmd+-', 'cmd+0', 'cmd+opt+t', 'cmd+l', 'cmd+opt+l', 'cmd+f', 'cmd+,', 'cmd+r', 'cmd+shift+m',
  ]),
  'com.linear': freeze(['cmd+n', '/', 'g', 'cmd+shift+h', 'cmd+shift+,', 'cmd+.', 'cmd+,', 'cmd+shift+n', 'cmd+r', 'cmd+=', 'cmd+-', 'cmd+0']),
  'com.figma.Desktop': freeze([
    'cmd+n', 'cmd+opt+n', 'cmd+o', 'cmd+t', 'cmd+shift+t', 'ctrl+tab', 'ctrl+shift+tab', 'cmd+shift+n', 'shift+1', 'shift+2', 'shift+0',
    'cmd+=', 'cmd+-', 'opt+1', 'opt+2', 'cmd+\\', 'shift+r', 'cmd+y', 'ctrl+g', 'shift+d', 'cmd+shift+c', 'ctrl+shift+/',
  ]),
  'com.apple.iCal': freeze(['cmd+t']),
  'com.apple.Notes': freeze(['cmd+n', 'cmd+opt+f', 'cmd+f', 'cmd+3']),
  'com.apple.reminders': freeze(['cmd+f']),
  'com.apple.iWork.Keynote': freeze(['cmd+n']),
  'com.apple.finder': freeze(['cmd+shift+.', 'cmd+y']),
  'com.microsoft.Excel': freeze(['cmd+n', 'cmd+ctrl+f', 'shift+space', 'cmd+1']),
  'com.apple.FaceTime': freeze(['cmd+ctrl+f', 'cmd+r']),
  'com.apple.Photos': freeze(['cmd+i', 'space', 'cmd+ctrl+f', 'cmd+shift+i', 'cmd+n']),
  'com.spotify.client': freeze(['opt+shift+h', 'opt+shift+0', 'opt+shift+m', 'opt+shift+q', 'cmd+k', 'cmd+ctrl+f', 'cmd+0']),
});

/** Two-key sequences ("g s": key g, then key s at once), whose second key is never a bare pre-yes
 *  key on its own. Linear's go-to views, each one whose bare second key only opens a picker. Never
 *  "g i": a bare i assigns the issue to the person, so a dropped g writes before the yes. */
const SEQUENCES: Readonly<Record<string, readonly string[]>> = freeze({
  'com.linear': freeze(['g m', 'g a', 'g b', 'g v', 'g t', 'g x', 'g p', 'g s', 'g l']),
});

/** The menu paths each app may walk before a yes. A research path with a template ("View >
 *  {view}"), no top menu (WhatsApp's, Zoom's), or a single title that toggles (Spotify's Private
 *  Session, until its mark is read) is not listed: it waits for a yes. */
const MENUS: Readonly<Record<string, readonly string[]>> = freeze({
  'com.tinyspeck.slackmacgap': freeze(['Go > Later', 'View > Zoom In', 'View > Zoom Out', 'View > Actual Size']),
  'com.apple.mail': freeze([
    'View > Enable Message Filter', 'View > Disable Message Filter', 'View > Show Sidebar', 'View > Hide Sidebar', 'View > Show Favorites Bar',
    'View > Hide Favorites Bar', 'View > Organize by Conversation', 'View > Show Mail Categories', 'Message > Mark as Read',
    'Message > Mark as Unread', 'Message > Mute', 'Message > Unmute',
    // R3c: a view order, a flag colour and a private reminder on the selection, and the attach picker.
    'View > Sort By > Date', 'View > Sort By > From', 'View > Sort By > Subject', 'View > Sort By > Unread', 'View > Sort By > Size',
    'View > Sort By > Flags', 'View > Sort By > To', 'View > Sort By > Attachments', 'Message > Flag > Red', 'Message > Flag > Orange',
    'Message > Flag > Yellow', 'Message > Flag > Green', 'Message > Flag > Blue', 'Message > Flag > Purple', 'Message > Flag > Gray',
    'Message > Remind Me > Remind Me in 1 Hour', 'Message > Remind Me > Remind Me Tonight', 'Message > Remind Me > Remind Me Tomorrow',
    'File > Attach Files…',
  ]),
  'com.apple.MobileSMS': freeze([
    'Conversation > Hide Alerts', 'Conversation > Mark as Unread', 'Conversation > Pin', 'Conversation > Unpin', 'View > Make Text Bigger',
    'View > Make Text Smaller', 'View > Make Text Normal Size',
  ]),
  'com.google.Chrome': freeze([
    'Tab > Duplicate Tab', 'Tab > Pin Tab', 'Tab > Unpin Tab', 'Tab > Mute Site', 'Tab > Move Tab to New Window', 'View > Enter Full Screen',
    'View > Exit Full Screen', 'View > Always Show Bookmarks Bar', 'Window > Extensions',
    // R3c: each opens a new tab or a panel, never the page in front.
    'History > Show Full History', 'Window > Downloads', 'View > Developer > Developer Tools', 'View > Developer > JavaScript Console',
  ]),
  'com.apple.Safari': freeze([
    'Window > Pin Tab', 'Window > Unpin Tab', 'Window > Duplicate Tab', 'Window > Move Tab to New Window', 'Window > Merge All Windows',
    'Window > Mute Current Tab', 'View > Show Reader', 'View > Hide Reader', 'View > Enter Full Screen', 'View > Exit Full Screen',
    'View > Show Favorites Bar', 'View > Hide Favorites Bar', 'View > Show Reading List Sidebar', 'View > Show Sidebar', 'View > Hide Sidebar',
    'View > Stop', 'View > Show Downloads',
  ]),
  // A menu press never reaches the editor, where cmd+[ and cmd+] may outdent and indent.
  'notion.id': freeze(['View > Toggle Full Screen', 'History > Back', 'History > Forward']),
  'com.linear': freeze([
    'File > New Project…', 'View > Always Show Navigation Tabs', 'View > Enter Full Screen', 'Help > Keyboard Shortcuts Guide', 'History > Back',
    'History > Forward',
  ]),
  'com.figma.Desktop': freeze(['File > Open File URL From Clipboard', 'File > Show Version History']),
  'com.apple.iCal': freeze([
    'View > Next', 'View > Previous', 'Edit > Search', 'View > Refresh Calendars', 'View > By Day', 'View > By Week', 'View > By Month', 'View > By Year',
    'View > Show Calendar List', 'View > Hide Calendar List', 'View > Zoom In', 'View > Zoom Out',
  ]),
  'com.apple.Notes': freeze([
    'File > Pin Note', 'File > Unpin Note', 'File > Duplicate Note', 'View > as List', 'View > as Gallery', 'View > Show Sidebar', 'View > Hide Sidebar',
    'View > Sort By > Date Edited', 'View > Sort By > Date Created', 'View > Sort By > Title', 'View > Zoom In', 'View > Zoom Out', 'View > Actual Size',
  ]),
  // Never Edit > Flag (R3c): a flag on a reminder in a shared list is seen by everyone on it, so it waits for the yes.
  'com.apple.reminders': freeze([
    'View > Show Completed', 'View > Hide Completed', 'View > Show Sidebar', 'View > Hide Sidebar', 'View > as List', 'View > as Columns',
  ]),
  'com.apple.iWork.Keynote': freeze([
    'Play > Play Slideshow', 'Play > Rehearse Slideshow', 'View > Show Presenter Notes', 'View > Hide Presenter Notes', 'View > Navigator',
    'View > Slide Only', 'View > Light Table', 'View > Zoom > Zoom In', 'View > Zoom > Zoom Out', 'View > Zoom > Fit Slide', 'View > Zoom > Actual Size',
  ]),
  // Never Go > AirDrop: while its window is open the Mac is discoverable to people nearby.
  'com.apple.finder': freeze([
    'Go > Home', 'File > New Finder Window', 'File > New Tab', 'File > New Folder', 'Edit > Search', 'Go > Enclosing Folder', 'Go > Recents',
    'Go > Documents', 'Go > Desktop', 'Go > Downloads', 'Go > Network', 'Go > iCloud Drive', 'Go > Shared', 'Go > Applications', 'Go > Utilities',
    'View > as Icons', 'View > as List', 'View > as Columns', 'View > as Gallery', 'View > Show Path Bar', 'View > Hide Path Bar',
    'View > Show Status Bar', 'View > Hide Status Bar', 'View > Show Sidebar', 'View > Hide Sidebar', 'View > Show Preview', 'View > Hide Preview',
    'View > Show Tab Bar', 'View > Hide Tab Bar', 'View > Show Toolbar', 'View > Hide Toolbar', 'Go > Back', 'Go > Forward', 'Go > Connect to Server…',
  ]),
  'com.apple.Photos': freeze(['View > Hide Sidebar', 'View > Zoom In', 'View > Show Hidden Photo Album', 'View > Show Sidebar', 'View > Zoom Out']),
  'com.spotify.client': freeze(['View > Go Back', 'View > Zoom In', 'View > Zoom Out']),
});

/** A switcher: the opener that summons it and the exact labels its field is known by. Before a
 *  yes, Return presses only after a name typed into one of these (its Return goes somewhere and
 *  sends nothing): never a command palette or a status field. */
interface Switcher { opener: { chord: string } | { menu: string }; labelIn: readonly string[] }
const SWITCHERS: Readonly<Record<string, readonly Switcher[]>> = freeze({
  'com.tinyspeck.slackmacgap': freeze([
    freeze({ opener: freeze({ chord: 'cmd+k' }), labelIn: freeze(['search', 'jump to', 'go to']) }),
    freeze({ opener: freeze({ chord: 'cmd+g' }), labelIn: freeze(['search']) }),
    freeze({ opener: freeze({ chord: 'cmd+f' }), labelIn: freeze(['search']) }),
  ]),
  'com.apple.mail': freeze([
    freeze({ opener: freeze({ chord: 'cmd+opt+f' }), labelIn: freeze(['search']) }),
    freeze({ opener: freeze({ chord: 'cmd+shift+o' }), labelIn: freeze(['open quickly', 'mailbox', 'search']) }),
  ]),
  'notion.id': freeze([freeze({ opener: freeze({ chord: 'cmd+p' }), labelIn: freeze(['search']) })]),
});

/** The https hosts a link may open before a yes: the table's sites and the research's search. */
const URL_HOSTS: readonly string[] = freeze([
  'www.youtube.com', 'mail.google.com', 'www.google.com', 'github.com', 'x.com', 'www.instagram.com', 'www.reddit.com', 'www.linkedin.com',
  'www.notion.so', 'www.figma.com', 'maps.google.com', 'calendar.google.com', 'news.ycombinator.com', 'en.wikipedia.org',
]);

/** Paths a vetted host allows, where only some of its paths are safe: Google's home page, its search
 *  and its map search, never a redirect (/url) or anything else; Wikipedia's search page only. */
const HOST_PATHS: Readonly<Record<string, RegExp>> = freeze({
  'www.google.com': /^(?:\/|\/search|\/maps\/search\/(?:\{[a-z_]+\})?)?$/,
  'en.wikipedia.org': /^\/w\/index\.php$/,
});

/** Query params a vetted host's link must carry: Wikipedia's search opens a page whose title matches
 *  the words on its own unless the link asks for the list of results (fulltext=1). */
const HOST_QUERIES: Readonly<Record<string, RegExp>> = freeze({ 'en.wikipedia.org': /(?:^|&)fulltext=1(?:&|$)/ });

/** Meeting joins: a link that puts the person on a call is an irreversible act, on any host list. */
const MEETING_JOINS: readonly { host: RegExp; path: RegExp }[] = freeze([
  { host: /^(?:[a-z0-9-]+\.)?zoom\.us$/, path: /^\/(?:j|w|s|my)\b/ },
  { host: /^meet\.google\.com$/, path: /^\/./ },
  { host: /^teams\.microsoft\.com$/, path: /^\/l\/meetup-join/ },
  { host: /^teams\.live\.com$/, path: /^\/meet/ },
  { host: /^app\.slack\.com$/, path: /^\/huddle/ },
  { host: /^(?:[a-z0-9-]+\.)?whereby\.com$/, path: /^/ },
  { host: /^(?:[a-z0-9-]+\.)?webex\.com$/, path: /^\/(?:meet|join)/ },
]);

/** Live calls a browser tab holds at a URL no join above names: Zoom's web client, Teams on the web
 *  (the meeting rides its query or fragment) and Webex's web meeting. Matched on the path and past it. */
const MEETING_TABS: readonly { host: RegExp; rest: RegExp }[] = freeze([
  { host: /^(?:[a-z0-9-]+\.)?zoom\.us$/, rest: /^\/wc\// },
  // teams.cloud.microsoft is Teams' current web host: a join link redirects there.
  { host: /^teams\.(?:(?:microsoft|live)\.com|cloud\.microsoft)$/, rest: /^\/light-meetings\/|meetingjoin=true|\/l\/meetup-join\/|#\/meet\// },
  { host: /^(?:[a-z0-9-]+\.)?webex\.com$/, rest: /^\/(?:wbxmjs|webappng)\// },
]);

const has = (table: Readonly<Record<string, readonly string[]>>, app: string, v: string): boolean =>
  Object.prototype.hasOwnProperty.call(table, app) && table[app]!.includes(v);

/** A canonical chord this app may be sent before a yes. */
export function isPreYesChord(app: string, chord: string): boolean {
  return has(CHORDS, app, chord);
}

/** `second`, pressed right after `first`, completes one of this app's vetted sequences. */
export function isPreYesSequence(app: string, first: string, second: string): boolean {
  return has(SEQUENCES, app, `${first} ${second}`);
}

/** A menu path (titles joined by " > ") this app may walk before a yes. */
export function isPreYesMenu(app: string, path: string): boolean {
  return has(MENUS, app, path);
}

/** The opener and the field labels are exactly one of this app's switchers. */
export function isSwitcher(app: string, opener: { chord: string } | { menu: string }, labelIn: readonly string[]): boolean {
  if (!Object.prototype.hasOwnProperty.call(SWITCHERS, app)) return false;
  const labels = [...new Set(labelIn.map((l) => l.trim().toLowerCase()))].sort().join('\n');
  return SWITCHERS[app]!.some((s) =>
    ('chord' in s.opener && 'chord' in opener ? s.opener.chord === opener.chord : 'menu' in s.opener && 'menu' in opener && s.opener.menu === opener.menu) &&
    [...s.labelIn].sort().join('\n') === labels);
}

/** An https link (template or filled) a recipe may open before a yes: a vetted host, never a
 *  meeting join. A host with a slot in it is the person's, so it waits for the yes. */
export function isPreYesUrl(url: string): boolean {
  const m = /^https:\/\/([^/?#]*)([^?#]*)/i.exec(url);
  if (!m) return false;
  const host = m[1]!.toLowerCase();
  const path = m[2] ?? '';
  if (MEETING_JOINS.some((j) => j.host.test(host) && j.path.test(path))) return false;
  if (Object.prototype.hasOwnProperty.call(HOST_PATHS, host) && !HOST_PATHS[host]!.test(path)) return false;
  if (Object.prototype.hasOwnProperty.call(HOST_QUERIES, host) && !HOST_QUERIES[host]!.test(/\?([^#]*)/.exec(url)?.[1] ?? '')) return false;
  // Google's "I'm feeling lucky" jumps straight to the first result's own host.
  if (host === 'www.google.com' && /[?&]btnI(?:[=&#]|$)/i.test(url)) return false;
  return URL_HOSTS.includes(host);
}

/** A URL (a tab's or a link's) that is a call or its join: reloading it, stepping back or forward
 *  from it, pointing it elsewhere or closing it leaves the call. Host and path, in any case. */
export function isMeetingUrl(url: string): boolean {
  const m = /^https?:\/\/(?:[^/?#@]*@)?([^/?#:]*)(?::\d*)?([^?#]*)(.*)/i.exec(url.trim());
  if (!m) return false;
  const host = m[1]!.toLowerCase();
  const path = m[2]!.toLowerCase();
  const rest = path + m[3]!.toLowerCase();
  return MEETING_JOINS.some((j) => j.host.test(host) && j.path.test(path)) ||
    MEETING_TABS.some((t) => t.host.test(host) && t.rest.test(rest));
}

/** A copy of the catalog, for review and its tests: changing it changes nothing. */
export function preYesCatalog(): {
  chords: Record<string, string[]>; menus: Record<string, string[]>; sequences: Record<string, string[]>; hosts: string[];
  switchers: Record<string, Array<{ opener: string; labelIn: string[] }>>;
} {
  const copy = (t: Readonly<Record<string, readonly string[]>>) => Object.fromEntries(Object.entries(t).map(([k, v]) => [k, [...v]]));
  const switchers = Object.fromEntries(Object.entries(SWITCHERS).map(([k, v]) => [k, v.map((s) => ({ opener: 'chord' in s.opener ? s.opener.chord : s.opener.menu, labelIn: [...s.labelIn] }))]));
  return { chords: copy(CHORDS), menus: copy(MENUS), sequences: copy(SEQUENCES), hosts: [...URL_HOSTS], switchers };
}
